/**
 * Can this channel actually deliver *right now*?
 *
 * `enabled` is not the same as usable: a freshly connected Telegram channel has no
 * enrolled chats until someone `/start`s the bot, and an email channel (including
 * the auto-provisioned system one, seeded from an owner who may not exist) can have
 * an empty recipient list. Both are enabled, both dispatch to a `skipped` delivery.
 *
 * That distinction is load-bearing for the billing alerts: their `*_notified_at`
 * flag is claimed BEFORE dispatch, so an alert skipped at the adapter is not
 * retried — it is lost until the wallet re-arms. Callers that can fall back to
 * another channel must therefore check this first.
 */

import type { NotificationChannelInstance, NotificationChannelType } from './types'

export function isChannelConfigDeliverable(
  type: NotificationChannelType,
  config: Record<string, unknown> | null | undefined,
): boolean {
  const cfg = config ?? {}

  if (type === 'email') {
    const recipients = cfg.recipients
    return Array.isArray(recipients) && recipients.some((r) => typeof r === 'string' && r.trim())
  }

  // Telegram: the bot cannot message a chat first, so it needs both a token and at
  // least one chat that has started it.
  const chats = cfg.chats
  return (
    typeof cfg.bot_token_encrypted === 'string' &&
    cfg.bot_token_encrypted.length > 0 &&
    Array.isArray(chats) &&
    chats.length > 0
  )
}

export function isChannelDeliverable(instance: NotificationChannelInstance): boolean {
  return instance.enabled && isChannelConfigDeliverable(instance.type, instance.config)
}
